import { includes } from 'lodash/fp';
import type * as React from 'react';
import { useMemo } from 'react';
import type { CycleFeedbackResponseDto } from '../../../../../../types/FeedbackResponseDto';
import API from '../../../../../api';
import * as R from '../../../../../routes';
import RouteError from '../../../../../routing/RouteError/index';
import AdminHelpList from '../../../../components/AdminHelpList';
import Button from '../../../../components/Button';
import PageTitle from '../../../../components/PageTitle';
import triggerPopupAlert from '../../../../components/PopupAlert/trigger';
import Redirect from '../../../../components/Redirect';
import { Container } from '../../../../components/Zen/Container';
import { ErrorMessage } from '../../../../components/Zen/ErrorMessage';
import { handleApiErrors, withData } from '../../../../hoc/withData';
import withModal from '../../../../hoc/withModal';
import { useAppContext } from '../../../../hooks/useAppContext';
import r from '../../../../services/Replacement';
import { trackEvent } from '../../../../services/Tracking';
import { interpolateAppUrl } from '../../../../services/Url';
import { ShareAlert } from '../../../Feedback/components/ShareAlert';
import { closeFeedbackZenMode } from '../../../Feedback/services/url';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { getProvideWordings } from '../../services/wording';
import type { FeedbackZenProvideProps } from './presenter';

const api = API.cycleFeedbackResponse;
const ButtonWithModal = withModal(Button);

type OuterProps = {
    responseId: string;
    prevPath?: string;
};

type AddedProps = {
    response: CycleFeedbackResponseDto;
    component: React.ComponentType<FeedbackZenProvideProps>;
};

const FeedbackZenProvideContainer: React.FC<OuterProps & AddedProps> = ({
    component: FeedbackZenProvide,
    response,
    prevPath,
}) => {
    const currentUser = useAppContext().user;
    const actions = useMemo<FeedbackZenProvideProps['actions']>(
        () => ({
            updateAnswers: answers => api.answer(response.id, answers),
            share: () =>
                api.share(response.id).then(res => {
                    trackEvent({ category: '360_PROVIDE_SCREEN_V2', label: 'Share feedback' });
                    triggerPopupAlert(<ShareAlert response={res} />, 'success', { surviveNavigation: true });
                    return res;
                }),
            close: () => {
                closeFeedbackZenMode(currentUser, prevPath);
                trackEvent({ category: '360_PROVIDE_SCREEN_V2', label: 'Save and close' });
            },
        }),
        [prevPath, response.id, currentUser]
    );

    const wordings = getProvideWordings(response);
    return (
        <>
            {response.status === 'WRITTEN' && (
                <Redirect to={interpolateAppUrl(R.FEEDBACK_USER_VIEW_MULTIPLE, { id: response.requestId })} />
            )}
            <PageTitle title={wordings.pageTitle} />
            <Container>
                <FeedbackZenProvide response={response} actions={actions} />
            </Container>
        </>
    );
};

export type CycleFeedbackResponseError = {
    message: string;
    cycleId: string;
    adminsToContact: Array<string>;
};

export const individualizedErrors = {
    FEEDBACK_CYCLE_DEADLINE_PASSED: ({ error }: { error: CycleFeedbackResponseError }) => {
        const appContext = useAppContext();
        const isCurrentUserAnAdmin = includes(appContext.user.id, error.adminsToContact);
        return (
            <ErrorMessage
                illustrationSrc={require('../../../Feedback/feedback_deadline_passed.svg')}
                title="Deadline Passed"
                body={
                    isCurrentUserAnAdmin ? (
                        <p>
                            If you need more time to complete this {r('fr.singular')}, please extend the timeline in
                            this{' '}
                            <a
                                href={interpolateAppUrl(R.ADMIN_FEEDBACK_CYCLE_EDIT, {
                                    cycleId: error.cycleId,
                                })}
                            >
                                cycle's settings
                            </a>{' '}
                            first.
                        </p>
                    ) : (
                        <>
                            <p>
                                Please ask your cycle admins to extend the timeline for this {r('fr.singular')} period
                                if you need more time to complete yours.
                            </p>
                            <div className="button-container">
                                <ButtonWithModal
                                    modalProps={{
                                        body: () => (
                                            <AdminHelpList adminIds={error.adminsToContact}>
                                                {() => (
                                                    <>
                                                        <h1>View Admins</h1>{' '}
                                                    </>
                                                )}
                                            </AdminHelpList>
                                        ),
                                    }}
                                >
                                    View Admins
                                </ButtonWithModal>
                            </div>
                        </>
                    )
                }
            />
        );
    },
    FEEDBACK_REQUEST_NOT_IN_PROGRESS: ({ error }: { error: CycleFeedbackResponseError }) => {
        const appContext = useAppContext();
        const isCurrentUserAnAdmin = includes(appContext.user.id, error.adminsToContact);
        return (
            <ErrorMessage
                illustrationSrc={require('../../../Feedback/feedback_deadline_passed.svg')}
                title="Deadline Passed"
                body={
                    isCurrentUserAnAdmin ? (
                        <p>
                            If you'd like to contribute and share your feedback, please re-open the{' '}
                            {r('fr.review.short')} in this{' '}
                            <a
                                href={interpolateAppUrl(R.ADMIN_FEEDBACK_WITH_CYCLE, {
                                    cycleId: error.cycleId,
                                })}
                            >
                                cycle's overview page
                            </a>
                            .
                        </p>
                    ) : (
                        <>
                            <p>
                                A {r('manager')} or {r('general.hr')} admin has already marked this reviewee’s{' '}
                                {r('fr.singular')} complete.
                            </p>
                            <p>
                                Please ask your cycle admins to re-open the {r('fr.review.short')} if you’d like to
                                contribute and share your feedback.
                            </p>
                            <div className="button-container">
                                <ButtonWithModal
                                    modalProps={{
                                        body: () => (
                                            <AdminHelpList adminIds={error.adminsToContact}>
                                                {() => (
                                                    <>
                                                        <h1>View Admins</h1>{' '}
                                                    </>
                                                )}
                                            </AdminHelpList>
                                        ),
                                    }}
                                >
                                    View Admins
                                </ButtonWithModal>
                            </div>
                        </>
                    )
                }
            />
        );
    },
};

const errorComponent = handleApiErrors<OuterProps, CycleFeedbackResponseError>(individualizedErrors, RouteError);

export default withData<AddedProps, OuterProps>({
    observe: {
        response: ({ responseId }) => api.get.createObservable(responseId),
    },
    resolve: {
        component: () =>
            import(/* webpackChunkName: 'FeedbackProvideZen' */ './presenter').then(
                ({ FeedbackZenProvide }) => FeedbackZenProvide
            ),
    },
    pendingComponent: LoadingIndicator,
    errorComponent,
})(FeedbackZenProvideContainer);
